import useSWR, { type BareFetcher, type SWRConfiguration, type SWRResponse } from "swr";
import {
  isAccountInactiveError,
  isApiError,
  isAuthenticationError,
  isPermissionError,
  isNotFoundError,
  isRateLimitError,
} from "@/lib/api-client";
import { globalFetcher, requestApi } from "@/lib/api-client";

type ApiFetcher<T> = BareFetcher<T | undefined>;
type BaseSWRConfig = Pick<
  SWRConfiguration<unknown, Error, ApiFetcher<unknown>>,
  | "dedupingInterval"
  | "revalidateOnFocus"
  | "revalidateOnReconnect"
  | "shouldRetryOnError"
  | "errorRetryCount"
  | "errorRetryInterval"
>;

// SWR configuration for request deduplication
export const swrConfig: Readonly<BaseSWRConfig> = {
  dedupingInterval: 5000, // 5 seconds deduplication
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  shouldRetryOnError: (error: Error) => {
    // Check if it's an ApiError with structured status info
    if (isApiError(error)) {
      // Don't retry auth errors, account-inactive, forbidden, not found, or
      // rate limits
      return (
        !isAuthenticationError(error) &&
        !isAccountInactiveError(error) &&
        !isPermissionError(error) &&
        !isNotFoundError(error) &&
        !isRateLimitError(error)
      );
    }

    // Fallback to message-based detection for other Error types
    const message = error.message || "";
    // Don't retry on auth errors or rate limits
    return (
      !message.includes("401") && !message.includes("429") && !message.includes("Session expired")
    );
  },
  errorRetryCount: 2,
  errorRetryInterval: 1000,
};

/**
 * SWR hook for API calls with session cookie authentication
 * @param key - SWR cache key (URL path)
 * @returns SWR response with data, error, isLoading
 */
export function useApiSWR<T = unknown>(
  key: string | null,
  fetcher?: ApiFetcher<T>,
  options?: Partial<SWRConfiguration<T | undefined, Error, ApiFetcher<T>>>,
): SWRResponse<T | undefined, Error> {
  const wrappedFetcher: ApiFetcher<T> = fetcher ?? ((url: string) => globalFetcher<T>(url));

  const mergedConfig: SWRConfiguration<T | undefined, Error, ApiFetcher<T>> = {
    ...swrConfig,
    ...(options ?? {}),
  };

  return useSWR<T | undefined, Error>(key, wrappedFetcher, mergedConfig);
}

export interface TypedApiClient {
  get<T = unknown>(endpoint: string, headers?: Record<string, string>): Promise<T | undefined>;
  post<T = unknown, B = unknown>(
    endpoint: string,
    body?: B,
    headers?: Record<string, string>,
  ): Promise<T | undefined>;
  put<T = unknown, B = unknown>(
    endpoint: string,
    body?: B,
    headers?: Record<string, string>,
  ): Promise<T | undefined>;
  patch<T = unknown, B = unknown>(
    endpoint: string,
    body?: B,
    headers?: Record<string, string>,
  ): Promise<T | undefined>;
  delete<T = unknown>(endpoint: string, headers?: Record<string, string>): Promise<T | undefined>;
}

/**
 * API client for direct API calls (POST, PUT, DELETE)
 */
export function useApiClient(): TypedApiClient {
  return {
    get: <T = unknown>(endpoint: string, headers?: Record<string, string>) =>
      requestApi<T>("GET", endpoint, undefined, headers),

    post: <T = unknown, B = unknown>(
      endpoint: string,
      body?: B,
      headers?: Record<string, string>,
    ) => requestApi<T, B>("POST", endpoint, body, headers),

    put: <T = unknown, B = unknown>(endpoint: string, body?: B, headers?: Record<string, string>) =>
      requestApi<T, B>("PUT", endpoint, body, headers),

    patch: <T = unknown, B = unknown>(
      endpoint: string,
      body?: B,
      headers?: Record<string, string>,
    ) => requestApi<T, B>("PATCH", endpoint, body, headers),

    delete: <T = unknown>(endpoint: string, headers?: Record<string, string>) =>
      requestApi<T>("DELETE", endpoint, undefined, headers),
  };
}
